import { EntryCondition } from './EntryCondition';
import { LeaveCondition } from './LeaveCondition';
import { TStateItem, TStates } from './StateMachine.types';
import EventsCenter from '@Managers/Events/EventsCenter';

export class StateMachine<E extends string> {
    private currentState: E;
    private currentSubState: E | undefined;
    private readonly states: TStates<E>;

    private readonly onStateChange?: (state: E) => void;
    private readonly onSubStateChange?: (subState: E | undefined) => void;

    constructor(
        states: TStates<E>,
        stateConditions: Record<string, LeaveCondition<E> | EntryCondition<E>>,
        hooks?: {
            onStateChange?: (state: E) => void;
            onSubStateChange?: (subState: E | undefined) => void;
        }
    ) {
        this.states = states;
        this.onStateChange = hooks?.onStateChange;
        this.onSubStateChange = hooks?.onSubStateChange;

        this.stateKeys.forEach((state) => {
            this.states[state]!.entryConditions ??= [];
            this.states[state]!.leaveConditions ??= [];
        });

        for (const key of Object.keys(stateConditions)) {
            this.assignCondition(stateConditions[key]);
        }
    }

    public get stateKeys(): E[] {
        return Object.keys(this.states) as E[];
    }

    public get currentSubStateKeys(): E[] {
        return Object.keys(this.states[this.currentState]!.subStates!) as E[];
    }

    public assignCondition(condition: LeaveCondition<E> | EntryCondition<E>): void {
        if (!condition.gameState) throw new Error('Cannot assign condition to undefined state');

        const stateItem = condition.gameSubState ? this.getSubstate(condition.gameState, condition.gameSubState) : this.getState(condition.gameState);

        if (stateItem) {
            if (condition instanceof LeaveCondition) {
                stateItem.leaveConditions ??= [];
                stateItem.leaveConditions.push(condition);
            }

            if (condition instanceof EntryCondition) {
                stateItem.entryConditions ??= [];
                stateItem.entryConditions.push(condition);
            }
        } else {
            throw new Error('Cannot assign condition to state: ' + condition.gameState + ' substate: ' + condition.gameSubState);
        }
    }

    public removeCondition(condition: LeaveCondition<E> | EntryCondition<E>): void {
        if (!condition.gameState) throw new Error('Cannot remove condition from undefined state');

        const stateItem = condition.gameSubState ? this.getSubstate(condition.gameState, condition.gameSubState) : this.getState(condition.gameState);

        if (!stateItem) {
            throw new Error('Cannot remove condition from state: ' + condition.gameState + ' substate: ' + condition.gameSubState);
        }

        const conditions = condition instanceof LeaveCondition ? stateItem.leaveConditions : stateItem.entryConditions;

        const index = conditions?.findIndex((assignedCondition) => assignedCondition === condition) ?? -1;

        if (index !== -1) conditions!.splice(index, 1);
    }

    public assignResolver(state: E, resolver: () => E, subState?: E): void {
        const stateItem = subState ? this.getSubstate(state, subState) : this.getState(state);
        if (stateItem) {
            stateItem.resolver = resolver;
        } else {
            throw new Error('Cannot assign resolver to state: ' + state);
        }
    }

    public assignSubStates(state: E, subStates: TStates<E>): void {
        const stateItem = this.getState(state);
        if (stateItem) {
            stateItem.subStates = { ...stateItem.subStates, ...subStates };
        } else {
            throw new Error('Cannot assign substates to state: ' + state);
        }
    }

    public getStates() {
        return this.states;
    }

    private getNextState(state: E, isSubState: boolean): E | undefined {
        const checkResolver = state === this.currentState || state === this.currentSubState;
        const stateItem = isSubState ? this.getSubstate(this.currentState, state) : this.getState(state);

        let nextState;
        if (stateItem?.resolver && checkResolver) {
            nextState = stateItem?.resolver?.();
        }
        if (!nextState) {
            const currentIndex = isSubState ? this.currentSubStateKeys.indexOf(state) : this.stateKeys.indexOf(state);
            nextState = isSubState ? this.currentSubStateKeys[currentIndex + 1] : this.stateKeys[currentIndex + 1];
            if (!nextState) return;
        }

        const nextItem = isSubState ? this.getSubstate(this.currentState, nextState) : this.getState(nextState);
        const canEntryNextState = nextItem?.entryConditions?.every((condition) => condition.check());

        return canEntryNextState ? nextState : this.getNextState(nextState, isSubState);
    }

    async setGameState(state: E): Promise<void> {
        if (!state) return console.error('Wrong state has been passed to StateManager');
        this.currentState = state;

        this.onStateChange?.(this.currentState);

        EventsCenter.emit(this.currentState);

        await this.checkLeaveConditions(this.getState(this.currentState));

        this.getState(state)?.leaveConditions!.forEach((condition) => condition.reset());

        if (this.states[this.currentState]?.subStates) {
            const nextSubState = this.findFirstValidSubState();

            if (nextSubState) {
                await this.setGameSubState(nextSubState);
            }
        }

        this.currentSubState = undefined;

        const nextState = this.getNextState(this.currentState, false);

        nextState ? this.setGameState(nextState) : console.error('Not existing next state');
    }

    async setGameSubState(state: E): Promise<void> {
        if (!state) return console.error('Wrong subState has been passed to StateManager');
        this.currentSubState = state;

        this.onSubStateChange?.(this.currentSubState);
        EventsCenter.emit(this.currentSubState);

        await this.checkLeaveConditions(this.getSubstate(this.currentState, state));
        this.getSubstate(this.currentState, state)?.leaveConditions!.forEach((condition) => condition.reset());

        const nextSubState = this.getNextState(this.currentSubState, true);
        if (nextSubState) await this.setGameSubState(nextSubState);
    }

    private findFirstValidSubState(): E | undefined {
        const subStateKeys = this.currentSubStateKeys;
        if (!subStateKeys.length) return;

        const first = subStateKeys[0];
        const canEnterFirst = this.getSubstate(this.currentState, first)?.entryConditions?.every((c) => c.check());

        return canEnterFirst ? first : this.getNextState(first, true);
    }

    async checkLeaveConditions(stateItem: TStateItem<E> | undefined): Promise<void[] | undefined> {
        if (!stateItem) return;
        return Promise.all(stateItem.leaveConditions!.map((condition) => condition.promise));
    }

    public getState(state: E): TStateItem<E> | undefined {
        return this.states[state];
    }

    public getSubstate(parentState: E, substate: E): TStateItem<E> | undefined {
        return this.states[parentState]?.subStates?.[substate];
    }

    public getCurrentState(): E {
        return this.currentState;
    }
}
